import { StatusEnumAccessRequest } from "@/enum/access_request";
import { useUploadFileMutation } from "@/services/file";
import { useUpdatedRequestMutation } from "@/services/request-access";
import { validateDocumentFormat } from "@/utils/validateDocument";
import {
  FilePreviewDialog,
  FileThumbnail,
  usePreviewFiles,
} from "components/file-preview";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { toast } from "components/ui/use-toast";
import { Upload } from "lucide-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";

interface CardContractProps {
  accessRequestCompleted: boolean | undefined;
}

export const CardContract = ({ accessRequestCompleted }: CardContractProps) => {
  const { id_request } = useParams();

  const { files, setLocalFile } = usePreviewFiles(
    "ACCESS_REQUEST",
    id_request?.toString() || "",
  );

  const [uploadFile] = useUploadFileMutation();
  const [updatedRequest] = useUpdatedRequestMutation();

  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(
    null,
  );

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    position: number,
  ) => {
    const validFiles = validateDocumentFormat(event);
    if (!validFiles) return;
    try {
      await uploadFile({
        file: validFiles[0],
        entity_name: "ACCESS_REQUEST",
        entity_id: id_request?.toString() || "",
      }).unwrap();

      await updatedRequest({
        comment: "Um novo arquivo foi enviado.",
        id: id_request?.toString() || "",
        status: StatusEnumAccessRequest.PENDING,
      }).unwrap();

      toast({
        variant: "success",
        description: "Arquivo enviado com sucesso.",
      });

      setLocalFile(position, {
        name: validFiles[0].name,
        url: URL.createObjectURL(validFiles[0]),
      });
    } catch (error) {
      toast({
        variant: "error",
        description: "Erro ao enviar arquivo.",
      });
      console.error(error);
    }
  };

  const handlePreviewClick = (index: number) => setSelectedFileIndex(index);

  const renderFilePreview = (index: number) => {
    if (files[index]) {
      return (
        <FileThumbnail
          key={index}
          file={files[index]}
          onClick={() => handlePreviewClick(index)}
        />
      );
    }

    return (
      <label
        className={
          "flex aspect-square w-full items-center justify-center rounded-md border border-dashed " +
          (accessRequestCompleted
            ? "cursor-not-allowed hidden"
            : "cursor-pointer")
        }
      >
        <Upload className="h-6 w-6 text-muted-foreground" />
        <span className="mt-2 text-sm font-medium text-muted-foreground">
          Upload
        </span>
        <input
          type="file"
          accept=".pdf,.doc,.docx"
          className="hidden"
          onChange={(event) => handleFileUpload(event, index)}
          disabled={accessRequestCompleted}
        />
      </label>
    );
  };

  const renderRemainingFiles = () => {
    if (files.length > 3) {
      const remaining = files.length - 3;
      return (
        <div
          className="relative flex h-full w-full items-center justify-center rounded-md border pointer"
          onClick={() => handlePreviewClick(3)}
        >
          <span className="text-muted-foreground text-lg font-medium">
            +{remaining}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>Termos</CardTitle>
        <CardDescription className="flex  justify-between flex-col gap-4">
          Clique em qualquer área para anexar ou visualizar arquivos.
          <Button
            variant="link"
            className="w-[125px]"
            onClick={() => handlePreviewClick(1)}
          >
            Visualizar Arquivos
          </Button>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          {renderFilePreview(0)}
          <div className="grid grid-cols-3 gap-2">
            {renderFilePreview(1)}
            {renderFilePreview(2)}
            {files.length > 3 ? renderRemainingFiles() : renderFilePreview(3)}
          </div>
        </div>
      </CardContent>
      <FilePreviewDialog
        files={files}
        selectedIndex={selectedFileIndex}
        onSelectedIndexChange={setSelectedFileIndex}
      />
    </Card>
  );
};
